import React, { useState, useEffect, useContext } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    TextField,
    Autocomplete,
    IconButton,
    Divider,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon } from "@mui/icons-material";
import {
    createCompanyRequest,
    updateCompanyRequest,
    getBusinesses,
    CompanyRequest,
    CreateCompanyRequestPayload,
    CreateCompanyRequestRolePayload,
    Business,
} from "../../api/companyRequestsApi";
import { usersApi } from "../../api/usersApi";
import { User } from "../../models/worker";
import { SettingsContext } from "../../context/SettingsContext";
import { Profession } from "../../models/settings";
import logger from "../../utils/logger";

interface Props {
    /** Parent company request being edited; if null, this is a create flow. */
    initialRequest?: CompanyRequest | null;
    onSuccess: () => void;
    onCancel: () => void;
}

interface RoleDraft {
    professionKey: string;
    numberOfWorkersNeeded?: number;
    minYearsExperience?: number;
    requirementsDescription?: string;
}

export default function CompanyRequestForm({ initialRequest, onSuccess, onCancel }: Props) {
    const { settings } = useContext(SettingsContext);
    const professionOptions: Profession[] = settings?.professions ?? [];
    const isEdit = !!initialRequest;

    const [loading, setLoading] = useState(false);
    const [businessOptions, setBusinessOptions] = useState<Business[]>([]);
    const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
    const [accountManagerOptions, setAccountManagerOptions] = useState<User[]>([]);
    const [selectedAccountManager, setSelectedAccountManager] = useState<User | null>(null);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [location, setLocation] = useState("");
    const [hoursPerWeek, setHoursPerWeek] = useState("");

    // Roles only matter on create. On edit, the parent form only edits parent fields;
    // role-level edits live on the detail page.
    const [roles, setRoles] = useState<RoleDraft[]>([
        { professionKey: "", numberOfWorkersNeeded: 1, minYearsExperience: undefined, requirementsDescription: "" },
    ]);

    useEffect(() => {
        if (initialRequest) {
            setSelectedBusiness(initialRequest.businessId ? { id: initialRequest.businessId, name: initialRequest.businessName || "" } : null);
            setStartDate(initialRequest.startDate ? initialRequest.startDate.split("T")[0] : "");
            setEndDate(initialRequest.endDate ? initialRequest.endDate.split("T")[0] : "");
            setLocation(initialRequest.location ?? "");
            setHoursPerWeek(initialRequest.hoursPerWeek ?? "");
        }
        loadBusinesses();
        loadAccountManagers();
    }, [initialRequest]);

    useEffect(() => {
        if (initialRequest?.accountManagerId && accountManagerOptions.length > 0) {
            const match = accountManagerOptions.find((u) => u.keycloakId === initialRequest.accountManagerId);
            setSelectedAccountManager(match ?? null);
        }
    }, [accountManagerOptions, initialRequest]);

    const loadBusinesses = async () => {
        try {
            const businesses = await getBusinesses({ limit: 100 });
            setBusinessOptions(businesses);
        } catch {
            logger.error("Failed to load businesses");
        }
    };

    const loadAccountManagers = async () => {
        try {
            const users = await usersApi.getRecruitmentUsers();
            setAccountManagerOptions(users.filter((u) => !!u.keycloakId));
        } catch {
            logger.error("Failed to load account managers");
        }
    };

    const handleAddRole = () => {
        setRoles([
            ...roles,
            { professionKey: "", numberOfWorkersNeeded: 1, minYearsExperience: undefined, requirementsDescription: "" },
        ]);
    };

    const handleRemoveRole = (index: number) => {
        setRoles(roles.filter((_, i) => i !== index));
    };

    const handleRoleChange = (index: number, field: keyof RoleDraft, value: any) => {
        const updated = [...roles];
        updated[index] = { ...updated[index], [field]: value };
        setRoles(updated);
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            if (isEdit && initialRequest) {
                await updateCompanyRequest(initialRequest.id, {
                    businessId: selectedBusiness?.id,
                    businessName: selectedBusiness?.name,
                    accountManagerId: selectedAccountManager?.keycloakId || undefined,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                    location: location || undefined,
                    hoursPerWeek: hoursPerWeek || undefined,
                });
            } else {
                const payload: CreateCompanyRequestPayload = {
                    businessId: selectedBusiness?.id,
                    businessName: selectedBusiness?.name,
                    accountManagerId: selectedAccountManager?.keycloakId || undefined,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                    location: location || undefined,
                    hoursPerWeek: hoursPerWeek || undefined,
                    roles: roles.map<CreateCompanyRequestRolePayload>((r) => ({
                        professionKey: r.professionKey,
                        numberOfWorkersNeeded: r.numberOfWorkersNeeded,
                        minYearsExperience: r.minYearsExperience,
                        requirementsDescription: r.requirementsDescription || undefined,
                    })),
                };
                await createCompanyRequest(payload);
            }
            onSuccess();
        } catch {
            logger.error("Failed to save request");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <h2>{isEdit ? "Edit Request" : "New Company Request"}</h2>
                <IconButton onClick={onCancel} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

            <Card sx={{ mb: 2 }}>
                <CardHeader title="Company Information" />
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={businessOptions}
                                getOptionLabel={(opt) => opt.name}
                                value={selectedBusiness}
                                onChange={(_, value) => setSelectedBusiness(value)}
                                renderInput={(params) => <TextField {...params} label="Business" />}
                                noOptionsText="No businesses found"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={accountManagerOptions}
                                getOptionLabel={(opt) =>
                                    [opt.firstName, opt.lastName].filter(Boolean).join(" ") || opt.username
                                }
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                value={selectedAccountManager}
                                onChange={(_, value) => setSelectedAccountManager(value)}
                                renderInput={(params) => <TextField {...params} label="Account Manager" />}
                                noOptionsText={accountManagerOptions.length === 0 ? "No assignable users — run db:seed in optima-backend" : "No match"}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Start Date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="End Date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Location"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Hours per Week"
                                value={hoursPerWeek}
                                onChange={(e) => setHoursPerWeek(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {!isEdit && (
                <>
                    <Divider sx={{ my: 2 }}>Roles</Divider>
                    {roles.map((role, index) => (
                        <Card key={index} sx={{ mb: 2 }}>
                            <CardHeader
                                title={`Role ${index + 1}`}
                                action={
                                    roles.length > 1 && (
                                        <IconButton onClick={() => handleRemoveRole(index)} color="error" size="small">
                                            <DeleteIcon />
                                        </IconButton>
                                    )
                                }
                            />
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={professionOptions}
                                            getOptionLabel={(opt) => opt.professionName}
                                            isOptionEqualToValue={(opt, val) => opt.professionKey === val.professionKey}
                                            value={professionOptions.find((p) => p.professionKey === role.professionKey) ?? null}
                                            onChange={(_, value) => handleRoleChange(index, "professionKey", value?.professionKey ?? "")}
                                            renderInput={(params) => <TextField {...params} label="Profession" required />}
                                            noOptionsText={professionOptions.length === 0 ? "Loading professions..." : "No matching profession"}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Number of Workers Needed"
                                            value={role.numberOfWorkersNeeded ?? ""}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                const parsed = v === "" ? undefined : parseInt(v, 10);
                                                handleRoleChange(index, "numberOfWorkersNeeded", Number.isNaN(parsed as number) ? undefined : parsed);
                                            }}
                                            inputProps={{ min: 1 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Min Years Experience"
                                            value={role.minYearsExperience ?? ""}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                const parsed = v === "" ? undefined : parseInt(v, 10);
                                                handleRoleChange(index, "minYearsExperience", Number.isNaN(parsed as number) ? undefined : parsed);
                                            }}
                                            inputProps={{ min: 0 }}
                                            placeholder="Leave blank if not required"
                                        />
                                        <FormControlLabel
                                            sx={{ mt: 0.5 }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={role.minYearsExperience === undefined}
                                                    onChange={(e) =>
                                                        handleRoleChange(
                                                            index,
                                                            "minYearsExperience",
                                                            e.target.checked ? undefined : 0,
                                                        )
                                                    }
                                                />
                                            }
                                            label="No minimum experience required"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Requirements Description"
                                            value={role.requirementsDescription ?? ""}
                                            onChange={(e) => handleRoleChange(index, "requirementsDescription", e.target.value)}
                                            placeholder="Detailed requirements for AI scoring..."
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}

                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddRole} sx={{ mb: 2 }}>
                        Add Role
                    </Button>
                </>
            )}

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button onClick={onCancel} disabled={loading}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : isEdit ? "Update" : "Create"}
                </Button>
            </Box>
        </Box>
    );
}
